import { useCallback, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Swords, Trophy, Crown, Clock, Droplets, Hammer, Dumbbell, AlertTriangle, ChevronRight, Shield, Flame, Ticket, Search} from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, btn } from "@/components/Page";
import TournamentsPanel from "@/components/TournamentsPanel";
import TimedQuests from "@/components/TimedQuests";
import ConfirmDialog from "@/components/ConfirmDialog";
import SandsRun from "@/components/SandsRun";

// Custom Gladiator Trials Icon for main page header only
const TrialsIcon = ({ size = 24, className = "" }) => (
  <img 
    src="/trials-icon.png" 
    alt="Gladiator Trials" 
    className={`inline-block ${className}`}
    style={{ 
      width: size * 1.2,
      height: size * 1.2,
      objectFit: 'contain'
    }}
  />
);

function countdown(iso) {
  if (!iso) return "";
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "ready";
  const m = Math.floor(ms / 60000), h = Math.floor(m / 60);
  return h > 0 ? `${h}h ${m % 60}m` : `${m}m ${Math.floor((ms % 60000) / 1000)}s`;
}

function dayClock(iso) {
  if (!iso) return "00:00:00";
  let s = Math.max(0, Math.floor((new Date(iso) - Date.now()) / 1000));
  const h = Math.floor(s / 3600); s -= h * 3600;
  const m = Math.floor(s / 60); s -= m * 60;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(h)}:${p(m)}:${p(s)}`;
}

function formatBossReward(reward) {
  if (reward.type === "fragments") return `${reward.amount} ${reward.rarity} ${reward.kind} fragment${reward.amount !== 1 ? "s" : ""}`;
  if (reward.type === "item") return `${reward.amount}× ${reward.rarity} ${reward.kind}`;
  if (reward.type === "gold") return `${reward.amount} gold`;
  if (reward.type === "scouting") return `2× scouting for ${reward.days} days`;
  if (reward.type === "training_boost") return `+${reward.percent}% training for ${reward.days} days`;
  if (reward.type === "campaign_stamina") return `${reward.amount} Campaign Stamina Potion${reward.amount !== 1 ? "s" : ""}`;
  if (reward.type === "recovery_ether") return `${reward.amount} Recovery Ether${reward.amount !== 1 ? "s" : ""}`;
  return "";
}

function BossLeaderboard({ title, rows, valueKey, emptyText, dataTestId }) {
  return (
    <div data-testid={dataTestId} className="border border-zinc-800 bg-zinc-950 p-4">
      <h3 className="font-cinzel text-amber-300 text-sm uppercase tracking-wide mb-3">{title}</h3>
      {rows.length === 0 ? (
        <p className="text-sm text-zinc-600">{emptyText}</p>
      ) : (
        <ol className="space-y-1.5">
          {rows.map((row, index) => (
            <li key={`${title}-${row.name}-${index}`} className="flex items-center justify-between text-sm">
              <span className="text-zinc-300"><span className="text-zinc-600 mr-2 tabular-nums">{index + 1}.</span>{row.name}</span>
              <span className="text-amber-400 tabular-nums">{row[valueKey].toLocaleString()}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function SandsEaseCommunityEvent({ event, onOpenSands, onOpenLegacySands }) {
  if (!event?.active) return null;
  return <section data-testid="sands-ease-community-event" className="mb-6 border border-cyan-500/50 bg-cyan-950/20 p-5">
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-200">Active Event</div>
        <h2 className="mt-1 font-cinzel text-xl text-zinc-100">Sands Ease</h2>
        <p data-testid="sands-ease-community-event-copy" className="mt-2 max-w-2xl text-sm text-zinc-300">Sands Run opponents are {event.reduction_percent}% weaker, while every Legacy Sands reward is doubled, including armor finds. Expedition Kit reductions still stack with this event.</p>
        {event.active_until && <p data-testid="sands-ease-community-event-countdown" className="mt-2 text-xs text-cyan-200">Ends in {countdown(event.active_until)}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        <button data-testid="sands-ease-community-event-run-link" type="button" onClick={onOpenSands} className="border border-cyan-400/60 px-4 py-2 text-sm font-cinzel uppercase tracking-wide text-cyan-100 transition-colors hover:border-cyan-300 hover:bg-cyan-950/50">Open Sands Run</button>
        <button data-testid="sands-ease-community-event-legacy-link" type="button" onClick={onOpenLegacySands} className="border border-cyan-400/60 px-4 py-2 text-sm font-cinzel uppercase tracking-wide text-cyan-100 transition-colors hover:border-cyan-300 hover:bg-cyan-950/50">Open Legacy Sands</button>
      </div>
    </div>
  </section>;
}

function OreRushCommunityEvent({ event }) {
  if (!event?.active) return null;
  return <section data-testid="ore-rush-community-event" className="mb-6 border border-amber-500/60 bg-amber-950/25 p-5"><div className="flex flex-wrap items-center justify-between gap-4"><div><div className="text-[11px] uppercase tracking-[0.2em] text-amber-200">Active Event</div><h2 className="mt-1 font-cinzel text-xl text-zinc-100">Ore Rush</h2><p data-testid="ore-rush-community-event-copy" className="mt-2 max-w-2xl text-sm text-zinc-300">The mines are yielding rich seams. Every online expedition and offline auto-mining run gains 3× discovery chance and 2× raw ore fragments while Ore Rush remains active.</p>{event.active_until && <p data-testid="ore-rush-community-event-countdown" className="mt-2 text-xs text-amber-200">Ends in {countdown(event.active_until)}</p>}</div><a data-testid="ore-rush-community-event-mining-link" href="/mining" className="border border-amber-400/60 px-4 py-2 text-sm font-cinzel uppercase tracking-wide text-amber-100 transition-colors hover:border-amber-300 hover:bg-amber-950/50">Open Mining</a></div></section>;
}

export default function CommunityEvents() {
  const { refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState(() => {
    const t = new URLSearchParams(window.location.search).get("tab");
    return ["boss", "tournaments", "quests", "adventure", "legacy-sands"].includes(t) ? t : "tournaments";
  });
  const [busy, setBusy] = useState(false);
  const [feed, setFeed] = useState([]);
  const [, force] = useState(0);
  const tick = useRef(null);

  const load = useCallback(() => Promise.all([api.get("/boss"), api.get("/events/availability"), api.get("/sands-ease-event"), api.get("/mining/ore-rush-event")])
    .then(([bossResponse, availabilityResponse, sandsEaseResponse, oreRushResponse]) => setData({
      ...bossResponse.data,
      events_active: Boolean(availabilityResponse.data?.active),
      active_events: availabilityResponse.data?.active_events || [],
      sands_ease_event: sandsEaseResponse.data,
      ore_rush_event: oreRushResponse.data,
    }))
    .catch(() => setData(false)), []);
  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(timer); window.removeEventListener("focus", onFocus); };
  }, [load]);
  useEffect(() => { tick.current = setInterval(() => force((n) => n + 1), 1000); return () => clearInterval(tick.current); }, []);

  const strike = async () => {
    setBusy(true);
    try {
      const { data: res } = await api.post("/boss/strike");
      setFeed((f) => [{ t: Date.now(), txt: `Your ludus struck for ${res.damage} damage! +${Object.values(res.frags)[0]} ${Object.keys(res.frags)[0]} fragments.` }, ...f].slice(0, 8));
      if (res.killed && res.reward) {
        if (res.reward.eligible) { toast.success(`You felled the beast! Reward: ${res.reward.rewards_text}`, { duration: 7000 }); refreshUser(); }
        else toast(`The beast is slain! You landed ${res.my_strikes}/${res.reward.min_strikes} strikes — needed ${res.reward.min_strikes} to share the spoils.`, { duration: 7000 });
      } else {
        toast.success(`Struck for ${res.damage} damage!`);
      }
      load();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (data === null) return <Loader />;
  const boss = data?.boss;
  const eventsVisible = Boolean(data?.events_active || data?.ore_rush_event?.active);
  const visibleTab = tab === "boss" && !eventsVisible ? "tournaments" : tab;

  return (
    <div>
      <PageHeader title="Gladiator Trials" subtitle="Various trials for your gladiator to complete & compete!" icon={TrialsIcon} />

      <div data-testid="gladiator-trials-tabs" className="grid grid-cols-2 gap-2 mb-6 sm:flex sm:flex-wrap sm:border-b sm:border-zinc-800">
        <button data-testid="tab-tournaments" onClick={() => setTab("tournaments")}
          className={`w-full justify-center px-3 py-3 text-xs sm:w-auto sm:px-4 sm:py-2 sm:text-sm font-cinzel uppercase tracking-wide border transition-colors flex items-center gap-2 whitespace-normal ${visibleTab === "tournaments" ? "border-amber-500 text-amber-300 bg-amber-950/20" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
          Tournaments
        </button>
        {eventsVisible && (
          <button data-testid="tab-boss" onClick={() => setTab("boss")}
            className={`w-full justify-center px-3 py-3 text-xs sm:w-auto sm:px-4 sm:py-2 sm:text-sm font-cinzel uppercase tracking-wide border transition-colors flex items-center gap-2 whitespace-normal ${visibleTab === "boss" ? "border-amber-500 text-amber-300 bg-amber-950/20" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
            Events
          </button>
        )}
        <button data-testid="tab-quests" onClick={() => setTab("quests")}
          className={`w-full justify-center px-3 py-3 text-xs sm:w-auto sm:px-4 sm:py-2 sm:text-sm font-cinzel uppercase tracking-wide border transition-colors flex items-center gap-2 whitespace-normal ${visibleTab === "quests" ? "border-amber-500 text-amber-300 bg-amber-950/20" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
          Quests
        </button>
        <button data-testid="tab-adventure" onClick={() => setTab("adventure")}
          className={`w-full justify-center px-3 py-3 text-xs sm:w-auto sm:px-4 sm:py-2 sm:text-sm font-cinzel uppercase tracking-wide border transition-colors flex items-center gap-2 whitespace-normal ${visibleTab === "adventure" ? "border-amber-500 text-amber-300 bg-amber-950/20" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
          Sands Run
        </button>
        <button data-testid="tab-legacy-sands" onClick={() => setTab("legacy-sands")}
          className={`w-full justify-center px-3 py-3 text-xs sm:w-auto sm:px-4 sm:py-2 sm:text-sm font-cinzel uppercase tracking-wide border transition-colors flex items-center gap-2 whitespace-normal ${visibleTab === "legacy-sands" ? "border-amber-500 text-amber-300 bg-amber-950/20" : "border-zinc-800 text-zinc-500 hover:text-zinc-300"}`}>
          The Sands
        </button>
      </div>

      {visibleTab === "quests" ? <TimedQuests /> : visibleTab === "adventure" ? <SandsRun /> : visibleTab === "legacy-sands" ? <AdventureSection /> : visibleTab === "tournaments" ? <TournamentsPanel /> : (
      <>
      <SandsEaseCommunityEvent event={data.sands_ease_event} onOpenSands={() => setTab("adventure")} onOpenLegacySands={() => setTab("legacy-sands")} />
      <OreRushCommunityEvent event={data.ore_rush_event} />
      <BountySection />
      <RevengeSection />
      <ScoutEventSection />
      <HotSpringsSection />
      <ImperatorFavorSection />
      <WanderingVeteranSection />
      {!boss ? null : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Boss + strike */}
          <div className="lg:col-span-2 space-y-5">
            <div data-testid="boss-image" className="relative h-56 sm:h-72 overflow-hidden border border-red-900/40">
              <img src="/beast.jpg" alt={boss.name}
                className="w-full h-full object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/20 to-transparent" />
              <h2 className="absolute bottom-3 left-4 font-cinzel text-3xl text-red-200 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
                {boss.name}
              </h2>
            </div>
            <div data-testid="boss-card" className="border border-red-900/40 bg-gradient-to-b from-red-950/30 to-zinc-950 p-6">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-cinzel text-2xl text-red-300 tracking-wide">{boss.name}</h2>
                <span data-testid="boss-status" className={`text-[11px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${boss.status === "active" ? "text-red-400 border-red-600/50 bg-red-950/40" : "text-emerald-400 border-emerald-700/50 bg-emerald-950/30"}`}>
                  {boss.status === "active" ? "Active" : "Slain"}
                </span>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs text-zinc-400 mb-1">
                  <span className="uppercase tracking-widest">Health</span>
                  <span data-testid="boss-hp" className="tabular-nums text-zinc-200">{boss.hp.toLocaleString()} / {boss.max_hp.toLocaleString()}</span>
                </div>
                <div className="h-4 bg-zinc-800 rounded-sm overflow-hidden border border-zinc-700">
                  <div className="h-full bg-gradient-to-r from-red-700 to-red-500 transition-all" style={{ width: `${Math.round(boss.pct * 100)}%` }} />
                </div>
                <div className="flex items-center justify-between text-[11px] text-zinc-500 mt-1">
                  <span>{boss.participants} fighter{boss.participants === 1 ? "" : "s"} joined</span>
                  <span>{Math.round(boss.pct * 100)}% remaining</span>
                </div>
              </div>

              {boss.status === "active" ? (
                <>
                <button data-testid="strike-boss-btn" disabled={busy || !data.can_strike} onClick={strike}
                  className={`mt-5 w-full py-3 font-cinzel uppercase tracking-wide rounded-sm transition-colors flex items-center justify-center gap-2 ${
                    data.can_strike ? "bg-red-600 hover:bg-red-500 text-white" : "bg-zinc-800 text-zinc-500 cursor-not-allowed"}`}>
                  {data.has_gladiator === false ? "Recruit a gladiator to strike" : data.can_strike ? "Strike the Beast" : `Ludus recovering — ${countdown(data.next_strike_at)}`}
                </button>
                {data.has_gladiator === false && (
                  <p data-testid="no-gladiator-boss-msg" className="mt-2 text-center text-[11px] text-amber-400/80">You need at least one gladiator in your ludus to strike the beast.</p>
                )}
                </>
              ) : (
                <p className="mt-5 text-center text-emerald-400 font-cinzel">The beast has been slain by the community!</p>
              )}
              {boss.status === "active" && (
                <div data-testid="my-beast-contribution" className="mt-3 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[11px] text-zinc-400">
                  <span className="uppercase tracking-widest text-zinc-500">Your contribution</span>
                  <span data-testid="my-beast-hits">Hits <b className="ml-1 tabular-nums text-amber-300">{data.my_strikes || 0}</b></span>
                  <span data-testid="my-beast-damage">Damage <b className="ml-1 tabular-nums text-amber-300">{(boss.my_damage || 0).toLocaleString()}</b></span>
                  {boss.reward_mode === "strikes" && data.min_strikes ? (
                    <span data-testid="my-strikes" className={(data.my_strikes || 0) >= data.min_strikes ? "text-emerald-400" : "text-zinc-600"}>
                      {(data.my_strikes || 0) >= data.min_strikes ? "Spoils unlocked" : `${data.my_strikes || 0}/${data.min_strikes} strikes needed for spoils`}
                    </span>
                  ) : <span className="text-zinc-600">{boss.reward_rule}</span>}
                </div>
              )}
              <p className="mt-2 text-[11px] text-zinc-600 text-center">One strike every {data.cooldown_hours}h · does not affect arena cooldowns · stronger ludus hit harder (but everyone counts)</p>
            </div>

            {feed.length > 0 && (
              <div data-testid="strike-feed" className="border border-zinc-800 bg-zinc-950 p-4 space-y-1.5">
                {feed.map((f) => <p key={f.t} className="text-sm text-zinc-400">{f.txt}</p>)}
              </div>
            )}
          </div>

          {/* Side: reward + leaderboard */}
          <div className="space-y-5">
            <div className="border border-amber-900/30 bg-zinc-950/60 p-4">
              <h3 className="font-cinzel text-amber-300 text-sm uppercase tracking-wide mb-2">Slayer&apos;s Reward</h3>
              
              {boss.reward_mode === "multi_tier" && boss.reward_tiers ? (
                /* Multi-tier display */
                <div className="space-y-4">
                  <p className="text-sm text-zinc-400 mb-3">Earn rewards by meeting multiple criteria — bonuses stack!</p>
                  {boss.reward_tiers.map((tier, idx) => {
                    const tierMode = tier.mode;
                    let tierDesc = "";
                    if (tierMode === "top_damage") {
                      tierDesc = `Top ${tier.top_x || 10} by Damage`;
                    } else if (tierMode === "top_hits") {
                      tierDesc = `Top ${tier.top_x || 10} by Hits`;
                    } else {
                      tierDesc = `${tier.min_strikes || 5}+ Strikes`;
                    }
                    
                    return (
                      <div key={idx} className="border-l-2 border-amber-700/50 pl-3">
                        <div className="font-cinzel text-amber-400 text-xs uppercase tracking-wider mb-1.5">
                          Tier {idx + 1}: {tierDesc}
                        </div>
                        <ul className="text-sm text-amber-200/90 space-y-1">
                          {tier.rewards.map((r, i) => (
                            <li key={i} className="text-xs">
                              • {formatBossReward(r)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                  <p className="mt-3 text-[11px] text-zinc-500">Plus a few fragments every strike. {data.buff_until && <span className="text-emerald-400">Your training boost is active!</span>}</p>
                </div>
              ) : (
                /* Legacy single-tier display */
                <>
                  <p className="text-sm text-zinc-400" data-testid="reward-rule-text">{boss.reward_rule}. Share in the spoils when the beast falls:</p>
                  <ul className="mt-2 text-sm text-amber-200/90 space-y-1" data-testid="boss-rewards-list">
                    {(boss.rewards || []).map((r, i) => (
                      <li key={i}>• {formatBossReward(r)}</li>
                    ))}
                  </ul>
                  <p className="mt-2 text-[11px] text-zinc-500">Plus a few fragments every strike. {data.buff_until && <span className="text-emerald-400">Your training boost is active!</span>}</p>
                </>
              )}
            </div>

            <div className="space-y-4">
              <BossLeaderboard
                title="Top by Hits"
                rows={boss.top_hits || []}
                valueKey="hits"
                emptyText="No strikes yet — be the first!"
                dataTestId="boss-top-hits"
              />
              <BossLeaderboard
                title="Top by Damage"
                rows={boss.top_damage || boss.top || []}
                valueKey="damage"
                emptyText="No damage yet — be the first to strike!"
                dataTestId="boss-top-damage"
              />
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}

function ScoutEventSection() {
  const [ev, setEv] = useState(null);
  const load = () => api.get("/scout-event").then((r) => setEv(r.data)).catch(() => setEv(null));
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!ev || !ev.active) return null;

  return (
    <div data-testid="scout-event-section" className="mb-6 border border-sky-800/50 bg-gradient-to-br from-sky-950/30 to-zinc-950 rounded-sm overflow-hidden">
      <div className="relative h-40 sm:h-52 overflow-hidden">
        <img src="/og-image.png" alt="The Scouts Season" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <h2 className="absolute bottom-3 left-5 font-cinzel text-2xl sm:text-3xl text-sky-100 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] flex items-center gap-2">
          <Search size={26} /> The Scouts Season
        </h2>
      </div>
      <div className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-xs uppercase tracking-widest text-sky-300 border border-sky-700/50 bg-sky-950/40 px-2 py-0.5 rounded-sm">Active Event</span>
          <span data-testid="scout-event-countdown" className="flex items-center gap-1.5 text-sm text-sky-200 font-cinzel tabular-nums">
            <Clock size={15} /> Ends in {dayClock(ev.active_until)}
          </span>
        </div>
        <p className="text-sm text-zinc-400 mb-2">
          Word has spread through the ludi: the Imperial talent-hunters have descended on the provinces. With coin flowing from the Emperor's games, every scout, trainer, and old lanista is out combing the training grounds for hidden promise — so your fighters' true potential is read <span className="text-sky-300 font-semibold">{ev.multiplier}× faster</span> this week.
        </p>
        <p className="text-sm text-zinc-500">
          Put your rookies through their paces and send scouts while the season lasts — the range on their hidden potential will tighten far quicker than usual before the hunters move on.
        </p>
      </div>
    </div>
  );
}

function HotSpringsSection() {
  const [ev, setEv] = useState(null);
  const load = () => api.get("/hot-springs").then((r) => setEv(r.data)).catch(() => setEv(null));
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!ev || !ev.active) return null;

  return (
    <div data-testid="hot-springs-section" className="mb-6 border border-cyan-800/50 bg-gradient-to-br from-cyan-950/30 to-zinc-950 rounded-sm overflow-hidden">
      <div className="relative h-40 sm:h-52 overflow-hidden">
        <img src="/bathhouse.jpg" alt="The Hot Springs" className="w-full h-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <h2 className="absolute bottom-3 left-5 font-cinzel text-2xl sm:text-3xl text-cyan-100 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)] flex items-center gap-2">
          <Droplets size={26} /> The Hot Springs
        </h2>
      </div>
      <div className="p-5">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <span className="text-xs uppercase tracking-widest text-cyan-300 border border-cyan-700/50 bg-cyan-950/40 px-2 py-0.5 rounded-sm">Active Event</span>
          <span data-testid="hot-springs-countdown" className="flex items-center gap-1.5 text-sm text-cyan-200 font-cinzel tabular-nums">
            <Clock size={15} /> Closes in {dayClock(ev.active_until)}
          </span>
        </div>
        <p className="text-sm text-zinc-400">
          The bathhouse is open! Every gladiator's arena recovery is <span className="text-cyan-300 font-semibold">{ev.reduction_pct}% faster</span> while the springs flow — meaning shorter cooldowns between fights. Make the most of it before the steam clears.
        </p>
      </div>
    </div>
  );
}

function WanderingVeteranSection() {
  const [ev, setEv] = useState(null);
  const [pick, setPick] = useState("");
  const [skill, setSkill] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingAssignment, setPendingAssignment] = useState(null);
  const load = () => api.get("/wandering-veteran").then((r) => setEv(r.data)).catch(() => setEv(null));
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!ev || !ev.active) return null;

  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
  const en = ev.enrolled;
  const needsEnroll = !en || !en.glad_available;

  const enroll = async (gladId) => {
    setBusy(true);
    try { const r = await api.post("/wandering-veteran/enroll", { glad_id: gladId }); setEv(r.data); setPick(""); toast.success("Wandering Veteran assignment updated"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const confirmAssignment = () => {
    if (!pick) return toast.error("Pick a gladiator to train under the veteran");
    const gladiator = (ev.my_gladiators || []).find((g) => g.id === pick);
    if (gladiator) setPendingAssignment(gladiator);
  };
  const payFavor = async () => {
    if (!skill) return toast.error("Choose a skill to boost today");
    setBusy(true);
    try { const r = await api.post("/wandering-veteran/pay-favor", { skill }); setEv(r.data); toast.success(`Favor paid — +${ev.bonus_pct}% ${cap(skill)} training today!`); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="wandering-section" className="mb-6 border border-stone-700/60 bg-gradient-to-br from-stone-900/40 to-zinc-950 p-5 rounded-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h2 className="font-cinzel text-stone-200 tracking-wide flex items-center gap-2 text-lg"><Swords size={20} className="text-stone-300" /> The Wandering Veteran — {ev.veteran}</h2>
        <span className="text-xs text-zinc-400" data-testid="wandering-ends">ends {countdown(ev.ends_at)}</span>
      </div>
      <p className="text-sm text-zinc-400 mb-3 italic">{ev.veteran}, {ev.veteran_blurb} He grants <span className="text-stone-200 not-italic">+{ev.bonus_pct}% training</span> in one skill each day you pay favor — by completing a ranked fight with your chosen gladiator.</p>
      <div data-testid="wandering-day-countdown" className="flex items-center gap-2 mb-4 text-sm">
        <Clock size={15} className="text-stone-300" />
        <span className="text-zinc-400">Today's favor resets in</span>
        <span className="font-cinzel tabular-nums text-stone-100 text-base tracking-wider" data-testid="wandering-day-clock">{dayClock(ev.day_ends_at)}</span>
      </div>

      {needsEnroll ? (
        <div className="border border-stone-700/40 bg-stone-950/20 p-4 rounded-sm" data-testid="wandering-enroll">
          {en && !en.glad_available && <p className="text-xs text-amber-300/80 mb-2">Your enrolled gladiator is no longer available — choose another.</p>}
          <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">Choose a gladiator to train under {ev.veteran}</label>
          <div className="flex flex-wrap gap-2">
            <select data-testid="wandering-glad-select" value={pick} onChange={(e) => setPick(e.target.value)}
              className="flex-1 min-w-[180px] bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-stone-500 outline-none rounded-sm">
              <option value="">— select gladiator —</option>
              {(ev.my_gladiators || []).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <button data-testid="wandering-enroll-btn" onClick={confirmAssignment} disabled={busy}
              className="px-5 py-2.5 bg-stone-600 text-white font-cinzel uppercase tracking-wide text-sm hover:bg-stone-500 rounded-sm disabled:opacity-50">Train under {ev.veteran}</button>
          </div>
        </div>
      ) : (
        <div className="border border-stone-700/40 bg-stone-950/20 p-4 rounded-sm" data-testid="wandering-favor">
          <p className="text-sm text-zinc-300 mb-3">Training under <span className="text-stone-200 font-cinzel">{ev.veteran}</span>: <span className="text-stone-100">{en.glad_name}</span></p>
          {en.favor_paid ? (
            <div data-testid="wandering-favor-active" className="flex items-center gap-2 text-sm text-emerald-300">
              <Swords size={15} /> +{ev.bonus_pct}% <span className="text-stone-200">{cap(en.favor_skill)}</span> training active for {en.favor_glad_name || en.glad_name} today. Return tomorrow to pay favor again.
            </div>
          ) : en.fought_today ? (
            <div data-testid="wandering-pay">
              <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">{en.glad_name} fought today — choose a skill to boost <span className="text-emerald-400/80 normal-case tracking-normal">(favor ready)</span></label>
              <div className="flex flex-wrap gap-2">
                <select data-testid="wandering-skill-select" value={skill} onChange={(e) => setSkill(e.target.value)}
                  className="flex-1 min-w-[160px] bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-stone-500 outline-none rounded-sm">
                  <option value="">— select skill —</option>
                  {(ev.skills || []).map((s) => <option key={s} value={s}>{cap(s)}</option>)}
                </select>
                <button data-testid="wandering-pay-btn" onClick={payFavor} disabled={busy}
                  className="px-5 py-2.5 bg-stone-600 text-white font-cinzel uppercase tracking-wide text-sm hover:bg-stone-500 rounded-sm disabled:opacity-50">Pay favor</button>
              </div>
            </div>
          ) : (
            <p data-testid="wandering-need-fight" className="text-sm text-zinc-400">Complete a <span className="text-stone-200">ranked fight</span> with {en.glad_name} today, then return here to pay favor and claim your +{ev.bonus_pct}% training boost.</p>
          )}
          <div className="mt-4 pt-3 border-t border-stone-800/80">
            <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1.5">Change training gladiator</label>
            <div className="flex flex-wrap gap-2">
              <select data-testid="wandering-change-glad-select" value={pick} onChange={(e) => setPick(e.target.value)}
                className="flex-1 min-w-[180px] bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-stone-500 outline-none rounded-sm">
                <option value="">— select a new gladiator —</option>
                {(ev.my_gladiators || []).filter((g) => g.id !== en.glad_id).map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select>
              <button data-testid="wandering-change-glad-btn" onClick={confirmAssignment} disabled={busy || !pick}
                className="px-5 py-2.5 border border-stone-600 text-stone-200 font-cinzel uppercase tracking-wide text-sm hover:border-stone-400 hover:bg-stone-900/50 rounded-sm disabled:opacity-50">Change gladiator</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialog
        state={pendingAssignment ? {
          title: en ? "Change Veteran Assignment" : "Confirm Veteran Assignment",
          message: en
            ? `Assign ${pendingAssignment.name} to train under ${ev.veteran}? Any favor already paid today remains with ${en.favor_glad_name || en.glad_name}; the new gladiator must win a ranked fight before it can pay favor.`
            : `Assign ${pendingAssignment.name} to train under ${ev.veteran}? They must win a ranked fight before paying today's favor.`,
          confirmLabel: en ? "Change gladiator" : "Confirm assignment",
          node: <p data-testid="wandering-assignment-confirmation" className="text-xs text-stone-400">This assignment can be changed again while the Wandering Veteran remains active.</p>,
          onConfirm: () => { const gladId = pendingAssignment.id; setPendingAssignment(null); enroll(gladId); },
        } : null}
        busy={busy}
        onClose={() => setPendingAssignment(null)}
      />
    </div>
  );
}

function ImperatorFavorSection() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [ev, setEv] = useState(null);
  const load = () => api.get("/imperator").then((r) => setEv(r.data)).catch(() => setEv(null));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!ev || !ev.active) return null;

  const board = ev.board || [];
  return (
    <div data-testid="imperator-section" className="mb-6 border border-violet-900/50 bg-gradient-to-br from-violet-950/30 to-zinc-950 p-5 rounded-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <h2 className="font-cinzel text-violet-200 tracking-wide flex items-center gap-2 text-lg"><Crown size={20} className="text-violet-300" /> Imperator's Favor</h2>
        <span className="text-xs text-zinc-400" data-testid="imperator-ends">ends {countdown(ev.ends_at)}</span>
      </div>
      <p className="text-sm text-zinc-400 mb-4">
        The Imperator rewards bloodshed in the arena. Every ranked victory earns <span className="text-violet-200">+1 gold</span>, wins on a 5+ streak earn <span className="text-violet-200">+3 gold</span>, and toppling a higher-ranked gladiator you challenged adds <span className="text-violet-200">+2 more</span>.
      </p>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">
          <h3 className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Trophy size={13} className="text-violet-300/70" /> Favor Leaderboard</h3>
          {board.length === 0 ? (
            <p className="text-sm text-zinc-600" data-testid="imperator-board-empty">No favor earned yet — claim a ranked win to top the board!</p>
          ) : (
            <ol className="space-y-1.5" data-testid="imperator-board">
              {board.map((row, i) => {
                const me = row.user_id === user?.user_id;
                return (
                  <li key={row.user_id} data-testid={`imperator-row-${i}`}
                    className={`flex items-center justify-between text-sm border-b border-zinc-900 pb-1.5 ${me ? "text-violet-200" : "text-zinc-300"}`}>
                    <span className="flex items-center gap-2 min-w-0">
                      <span className={`font-cinzel w-5 text-center ${i === 0 ? "text-violet-300" : i === 1 ? "text-zinc-300" : i === 2 ? "text-violet-700" : "text-zinc-600"}`}>{i + 1}</span>
                      <button data-testid={`imperator-lanista-${i}`} onClick={() => navigate(`/profile/${row.user_id}`)}
                        className="truncate text-left hover:text-violet-300 hover:underline underline-offset-2 transition-colors">
                        {row.name}
                      </button>
                      {me && <span className="text-[10px] uppercase tracking-widest text-violet-400/80 -ml-1">you</span>}
                      <span className="text-[11px] text-zinc-600">· {row.wins} win{row.wins === 1 ? "" : "s"}</span>
                    </span>
                    <span className="flex items-center gap-1 text-amber-400 tabular-nums shrink-0"><GoldCoin size={13} />{row.gold.toLocaleString()}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
        <div className="border border-violet-900/40 bg-violet-950/15 p-3 rounded-sm self-start">
          <h3 className="text-[11px] uppercase tracking-widest text-violet-300/80 mb-2 flex items-center gap-1.5"><Crown size={13} /> Your Favor</h3>
          <div className="flex items-center gap-2" data-testid="imperator-mine">
            <GoldCoin size={20} className="text-amber-400" />
            <span className="font-cinzel text-3xl text-amber-300 tabular-nums">{(ev.mine?.gold || 0).toLocaleString()}</span>
          </div>
          <p className="text-[11px] text-zinc-500 mt-1">{ev.mine?.wins || 0} ranked win{(ev.mine?.wins || 0) === 1 ? "" : "s"} this event</p>
        </div>
      </div>
    </div>
  );
}

function BountySection() {
  const navigate = useNavigate();
  const [b, setB] = useState(null);
  const load = () => api.get("/bounties").then((r) => setB(r.data)).catch(() => setB(null));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!b || !b.active) return null;

  const openMine = (b.mine || []).filter((x) => x.status === "open");

  return (
    <div data-testid="bounty-section" className="mb-6 border border-yellow-900/50 bg-gradient-to-br from-yellow-950/30 to-zinc-950 p-5 rounded-sm">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <h2 className="font-cinzel text-yellow-300 tracking-wide flex items-center gap-2 text-lg"><GoldCoin size={20} /> Bounty Week</h2>
        <span className="text-xs text-zinc-400" data-testid="bounty-ends">ends {countdown(b.ends_at)}</span>
      </div>
      <p className="text-sm text-zinc-500 mb-4">
        Put gold on a rival's head — open any gladiator's profile and hit <span className="text-yellow-300">Place Bounty</span>. Defeat a bountied gladiator in a ranked fight to claim the pot. Unclaimed gold is refunded when the event ends.
      </p>

      <h3 className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Top Open Bounties</h3>
      {(b.board || []).length === 0 ? (
        <p className="text-sm text-zinc-600 mb-4" data-testid="bounty-board-empty">No bounties on the board yet — be the first to post one!</p>
      ) : (
        <div className="space-y-2 mb-4" data-testid="bounty-board">
          {b.board.map((row) => (
            <button key={row.target_glad} data-testid={`bounty-row-${row.target_glad}`} onClick={() => navigate(`/gladiator/${row.target_glad}`)}
              className="w-full flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-950/60 px-4 py-3 rounded-sm hover:border-yellow-700/60 transition-colors text-left">
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 truncate">{row.target_glad_name} {row.mine && <span className="text-[10px] uppercase tracking-widest text-yellow-400/80 ml-1">your bounty</span>}</div>
                <div className="text-[11px] text-zinc-500">Lanista: {row.target_owner_name || "Unknown"} · {row.count} bount{row.count === 1 ? "y" : "ies"}</div>
              </div>
              <span className="flex items-center gap-1.5 text-yellow-300 font-cinzel shrink-0"><GoldCoin size={15} className="text-yellow-400" /> {row.total}g</span>
            </button>
          ))}
        </div>
      )}

      {openMine.length > 0 && (
        <>
          <h3 className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2">Your Active Bounties</h3>
          <div className="flex flex-wrap gap-2" data-testid="my-bounties">
            {openMine.map((m) => (
              <button key={m.id} onClick={() => navigate(`/gladiator/${m.target_glad}`)}
                className="inline-flex items-center gap-1.5 text-xs border border-yellow-900/40 text-yellow-200 bg-yellow-950/20 px-3 py-1.5 rounded-sm hover:border-yellow-600 transition-colors">
                <GoldCoin size={12} /> {m.amount}g on {m.target_glad_name}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function RevengeSection() {
  const { refreshUser } = useAuth();
  const [rev, setRev] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get("/revenge").then((r) => setRev(r.data)).catch(() => setRev(null));
  useEffect(() => {
    load();
    const t = setInterval(load, 15000);          // self-heal: reflect event start/end without a manual reload
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t); window.removeEventListener("focus", onFocus); };
  }, []);
  if (!rev || !rev.active) return null;

  const take = async (d) => {
    setBusy(d.challenge_id);
    try {
      const { data } = await api.post(`/revenge/${d.challenge_id}`);
      if (data.avenged) toast.success(`Revenge! ${d.my_glad_name} crushed ${d.opponent_glad_name} — double salvage${data.bonus_gold ? ` + ${data.bonus_gold} gold` : ""}!`, { duration: 6000 });
      else toast(`${d.my_glad_name} fell again to ${d.opponent_glad_name}… the grudge deepens.`, { duration: 6000 });
      refreshUser(); load();
    } catch (e) { toast.error(apiErr(e)); load(); } finally { setBusy(null); }
  };

  const left = Math.max(0, (rev.daily_cap || 0) - (rev.daily_used || 0));
  return (
    <div data-testid="revenge-section" className="mb-6 border border-red-900/50 bg-gradient-to-br from-red-950/40 to-zinc-950 p-5 rounded-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-cinzel text-red-300 tracking-wide flex items-center gap-2 text-lg"><Swords size={20} /> Revenge Week</h2>
        <span className="text-xs text-zinc-400" data-testid="revenge-uses">{left} of {rev.daily_cap} revenges left today · ends {countdown(rev.ends_at)}</span>
      </div>
      <p className="text-sm text-zinc-400 mb-4">Settle the score with anyone who beat you in the last 7 days — no rank limits, no waiting for them to accept. A successful payback earns <span className="text-red-300">double Arena Salvage</span> plus grudge gold.</p>
      {rev.defeats.length === 0 ? (
        <p className="text-sm text-zinc-500" data-testid="revenge-empty">No recent defeats to avenge — your gladiators have been unbeaten lately. Carry on, champion.</p>
      ) : (
        <div className="space-y-2" data-testid="revenge-list">
          {rev.defeats.map((d) => (
            <div key={d.challenge_id} data-testid={`revenge-${d.challenge_id}`} className="flex flex-wrap items-center justify-between gap-3 border border-zinc-800 bg-zinc-950/60 px-4 py-3 rounded-sm">
              <div className="text-sm">
                <span className="text-red-300 font-semibold">{d.opponent_glad_name}</span>
                <span className="text-zinc-500"> ({d.opponent_name}) beat your </span>
                <span className="text-zinc-200">{d.my_glad_name}</span>
                {d.grudge_count > 1 && <span className="ml-2 text-[10px] uppercase tracking-widest text-red-400 border border-red-900/50 px-1.5 py-0.5">grudge ×{d.grudge_count}</span>}
                {d.bonus_gold > 0 && <span className="ml-2 text-xs text-amber-400">+{d.bonus_gold} gold on win</span>}
              </div>
              <button data-testid={`revenge-btn-${d.challenge_id}`} onClick={() => take(d)} disabled={!d.can_revenge || busy === d.challenge_id || left <= 0}
                title={!d.can_revenge ? (d.reason || "Unavailable") : left <= 0 ? "No revenges left today" : "Fight now"}
                className={`px-4 py-1.5 text-xs font-cinzel uppercase tracking-wide rounded-sm transition-colors ${d.can_revenge && left > 0 ? "bg-red-600 text-white hover:bg-red-500" : "bg-zinc-800 text-zinc-500 cursor-not-allowed"}`}>
                {busy === d.challenge_id ? "Fighting…" : !d.can_revenge ? (d.reason || "Unavailable") : "Get Revenge"}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const RARITY_DOT = { common: "bg-zinc-400", uncommon: "bg-emerald-400", rare: "bg-sky-400", elite: "bg-fuchsia-400", legendary: "bg-amber-400" };

function resetIn(iso) {
  if (!iso) return "";
  const ms = new Date(iso) - Date.now();
  if (ms <= 0) return "soon";
  const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function StreakBanner({ streak, bonusActive, toBonus, bonusPct, bonusDays }) {
  if (!streak && streak !== 0) return null;
  return (
    <div data-testid="adventure-streak" className={`mb-5 flex flex-wrap items-center justify-between gap-3 border px-4 py-3 rounded-sm ${bonusActive ? "border-amber-700/50 bg-amber-950/20" : "border-zinc-800 bg-zinc-950"}`}>
      <div className="flex items-center gap-2.5">
        <Flame size={20} className={bonusActive ? "text-amber-400" : "text-zinc-500"} />
        <div>
          <div className="text-sm text-zinc-200" data-testid="adventure-streak-count">
            <span className="font-cinzel text-amber-300">{streak}</span>-day streak
          </div>
          <div className="text-[11px] text-zinc-500">
            {bonusActive
              ? <span className="text-amber-300">+{bonusPct}% rewards active — keep your streak alive each day to hold it.</span>
              : <span>Play {toBonus} more day{toBonus === 1 ? "" : "s"} in a row to unlock <span className="text-amber-300">+{bonusPct}% rewards</span> (at {bonusDays}+ days).</span>}
          </div>
        </div>
      </div>
      {bonusActive && (
        <span className="text-[10px] uppercase tracking-widest text-amber-300 border border-amber-700/50 bg-amber-950/30 px-2 py-1 rounded-sm" data-testid="adventure-bonus-badge">
          +{bonusPct}% Bonus
        </span>
      )}
    </div>
  );
}

function Rewards({ rewards }) {
  if (!rewards) return null;
  const f = rewards.fragments;
  const items = rewards.items?.length ? rewards.items : rewards.item ? [rewards.item] : [];
  const has = rewards.gold > 0 || f || rewards.training || rewards.free_tournament_entry || items.length > 0 || rewards.follis > 0;
  return (
    <div data-testid="adventure-rewards" className="mt-4 border border-amber-900/40 bg-zinc-950 p-4 rounded-sm">
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-2">
        Spoils {rewards.streak_bonus && <span className="text-amber-300 normal-case tracking-normal flex items-center gap-1"><Flame size={11} /> +50% streak bonus applied</span>}{rewards.sands_ease_reward_multiplier > 1 && <span data-testid="adventure-sands-ease-reward-bonus" className="text-cyan-200 normal-case tracking-normal">Sands Ease {rewards.sands_ease_reward_multiplier}× rewards applied</span>}
      </div>
      {has ? (
        <div className="flex flex-wrap gap-2">
          {rewards.gold > 0 && <span className="flex items-center gap-1.5 text-sm text-amber-300 border border-amber-800/40 bg-amber-950/20 px-2.5 py-1 rounded-sm"><GoldCoin size={14} /> +{rewards.gold} gold</span>}
          {rewards.follis > 0 && <span className="flex items-center gap-1.5 text-sm text-amber-600 border border-amber-700/50 bg-amber-950/30 px-2.5 py-1 rounded-sm"><FollisCoin size={14} className="text-amber-600" /> +{rewards.follis} Follis</span>}
          {f && <span className="flex items-center gap-1.5 text-sm text-zinc-200 border border-zinc-800 bg-zinc-900/40 px-2.5 py-1 rounded-sm"><Hammer size={14} /> <span className={`w-2 h-2 rounded-full ${RARITY_DOT[f.rarity] || "bg-zinc-400"}`} /> +{f.amount} {f.rarity} {f.kind} frag{f.amount > 1 ? "s" : ""}</span>}
          {rewards.training && <span className="flex items-center gap-1.5 text-sm text-emerald-300 border border-emerald-800/40 bg-emerald-950/20 px-2.5 py-1 rounded-sm"><Dumbbell size={14} /> +{rewards.training.amount} {rewards.training.skill}</span>}
          {items.map((item) => <span key={item.id} data-testid={`adventure-item-${item.id}`} className="flex items-center gap-1.5 text-sm text-amber-100 border border-amber-500/60 bg-amber-900/25 px-2.5 py-1 rounded-sm">{item.kind === "weapon" ? <Swords size={14} className="text-amber-300" /> : <Shield size={14} className="text-amber-300" />} <span className={`w-2 h-2 rounded-full ${RARITY_DOT[item.rarity] || "bg-zinc-400"}`} /> {item.name} <span className="text-[11px] text-zinc-400 capitalize">({item.rarity} {item.kind})</span></span>)}
          {rewards.free_tournament_entry && <span data-testid="adventure-free-entry" className="flex items-center gap-1.5 text-sm text-emerald-200 border border-emerald-600/60 bg-emerald-900/30 px-2.5 py-1 rounded-sm"><Ticket size={14} className="text-emerald-300" /> {rewards.free_tournament_entry.amount > 1 ? `${rewards.free_tournament_entry.amount} free` : "Free"} {rewards.free_tournament_entry.name} tournament entr{rewards.free_tournament_entry.amount > 1 ? "ies" : "y"}!</span>}
        </div>
      ) : <p className="text-sm text-zinc-600 italic">No spoils this time — but every road teaches something.</p>}
      {rewards.penalty && (
        <div data-testid="adventure-penalty" className="mt-3 flex items-center gap-2 text-[12px] text-orange-300 border border-orange-900/40 bg-orange-950/20 px-3 py-2 rounded-sm">
          <AlertTriangle size={13} className="shrink-0" />
          {rewards.penalty.type === "cooldown"
            ? <span>Your gladiator took a wound — resting for ~{rewards.penalty.hours}h before the next bout.</span>
            : <span>Your equipped gear lost {rewards.penalty.pct}% durability in the ordeal.</span>}
        </div>
      )}
    </div>
  );
}

function AdventureSection() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pick, setPick] = useState("");

  const load = () => api.get("/adventure/today").then((r) => { setData(r.data); if (r.data.gladiators?.[0]) setPick((p) => p || r.data.gladiators[0].id); }).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const start = async () => {
    if (!pick) return toast.error("Choose a gladiator first");
    setBusy(true);
    try { const { data: run } = await api.post("/adventure/start", { glad_id: pick }); setData((d) => ({ ...d, run, can_start: false })); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const choose = async (choiceId) => {
    setBusy(true);
    try {
      const { data: run } = await api.post("/adventure/choose", { choice_id: choiceId });
      setData((d) => ({ ...d, run }));
      if (run.status === "completed") {
        window.dispatchEvent(new Event("lanista:refresh-badges"));
        
        // Check for rare injury event
        if (run.rewards?.injury_event?.occurred) {
          const injury = run.rewards.injury_event;
          toast.error(
            <div className="space-y-2">
              <div className="font-cinzel text-base text-red-300 flex items-center gap-2">
                <AlertTriangle size={16} /> {injury.lore.title}
              </div>
              <div className="text-sm text-zinc-200 leading-relaxed">{injury.lore.story}</div>
              <div className="mt-2 pt-2 border-t border-red-900/40">
                <div className="text-xs text-red-400 font-semibold mb-1">Scar Acquired: {injury.scar.name}</div>
                <div className="text-xs text-zinc-400">
                  {injury.scar.neg && <div className="text-red-300">-{injury.scar.pct}% {injury.scar.neg}</div>}
                  {injury.scar.pos && <div className="text-emerald-300">+{injury.scar.pct}% {injury.scar.pos}</div>}
                </div>
              </div>
            </div>,
            { duration: 12000, className: 'max-w-md' }
          );
        }
        
        if (run.rewards?.item) {
          toast.success(`A lucky find! You claimed ${run.rewards.item.name} — a ${run.rewards.item.rarity} ${run.rewards.item.kind}. It's in your armoury.`, { duration: 8000 });
        }
        if (run.rewards?.free_tournament_entry) {
          const entryAmount = run.rewards.free_tournament_entry.amount || 1;
          toast.success(`${entryAmount > 1 ? `${entryAmount} free` : "Free"} ${run.rewards.free_tournament_entry.name} tournament entr${entryAmount > 1 ? "ies" : "y"} won.`, { duration: 7000 });
        }
      }
    }
    catch (e) { toast.error(apiErr(e)); load(); } finally { setBusy(false); }
  };

  if (!data) return <Loader />;
  const run = data.run;
  const sandsEaseRewardMultiplier = Number(run?.sands_ease_reward_multiplier || (data.sands_ease_event?.active ? data.sands_ease_event.legacy_reward_multiplier : 1) || 1);

  return (
    <div data-testid="adventure-panel">
      {/* The Sands Banner */}
      <div className="relative mb-6 overflow-hidden border border-emerald-900/40 rounded-sm">
        <img 
          src="/the-sands-banner.png" 
          alt="The Sands - Desert Journey" 
          className="w-full h-44 sm:h-60 object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent" />
        <h2 className="absolute bottom-3 left-5 font-cinzel text-2xl sm:text-3xl text-emerald-200 tracking-wide drop-shadow-[0_2px_6px_rgba(0,0,0,0.9)]">
          The Sands
        </h2>
      </div>

      <p className="text-sm text-zinc-500 mb-5 max-w-2xl">Send one gladiator down a winding desert road each day; your choices and fighter strengths decide the tale and its rewards.</p>

      {sandsEaseRewardMultiplier > 1 && <div data-testid="legacy-sands-ease-banner" className="mb-5 border-l-2 border-cyan-500 bg-cyan-950/20 px-4 py-3">
        <div className="text-xs uppercase tracking-[0.18em] text-cyan-200">Sands Ease Event</div>
        <p data-testid="legacy-sands-ease-copy" className="mt-1 text-sm text-zinc-100">Every Legacy Sands reward is doubled for this adventure, including gold, Follis, fragments, training, tournament entries, and every equipment find—including armor.</p>
      </div>}

      <StreakBanner streak={data.streak} bonusActive={data.streak_bonus} toBonus={data.streak_to_bonus}
        bonusPct={data.streak_bonus_pct} bonusDays={data.streak_bonus_days} />

      {/* No run yet → picker */}
      {!run && (
        data.gladiators.length === 0 ? (
          <p className="text-sm text-zinc-600 italic">Recruit a gladiator before setting out on the sands.</p>
        ) : (
          <div data-testid="adventure-start" className="max-w-xl">
            <div className="border border-amber-900/30 bg-zinc-950 p-5 rounded-sm">
              <p className="text-sm text-zinc-400 mb-4">Choose a single gladiator to walk the path today. The journey favors their standout skills — and only one fighter may set out before the sands shift at dawn.</p>
              <label className="text-[11px] uppercase tracking-widest text-zinc-500">Your gladiator</label>
              <select data-testid="adventure-glad-select" value={pick} onChange={(e) => setPick(e.target.value)} disabled={busy}
                className="mt-1.5 w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-100 text-sm focus:border-amber-600 outline-none rounded-sm">
                {data.gladiators.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.cls}</option>)}
              </select>
              <button data-testid="adventure-embark-btn" onClick={start} disabled={busy}
                className={btn() + " w-full mt-4"}>
                {busy ? "Setting out…" : "Embark"}
              </button>
            </div>
          </div>
        )
      )}

      {/* Active run → story node */}
      {run && run.status === "active" && (
        <div data-testid="adventure-active" className="max-w-2xl">
          <div className="border border-amber-900/30 bg-zinc-950 rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-amber-900/20 flex items-center justify-between gap-2">
              <h2 className="font-cinzel text-amber-300 tracking-wide">{run.current.title}</h2>
              <span className="text-[11px] text-zinc-500">{run.glad_name}</span>
            </div>
            <div className="p-5">
              {run.path.length === 0 && <p className="text-sm text-zinc-400 italic mb-4">{run.intro}</p>}
              <p data-testid="adventure-node-text" className="text-zinc-200 mb-5 leading-relaxed">{run.current.text}</p>
              <div className="space-y-2">
                {run.current.choices.map((c) => (
                  <button key={c.id} data-testid={`adventure-choice-${c.id}`} disabled={busy || !c.available} onClick={() => choose(c.id)}
                    className={`w-full text-left px-4 py-3 border rounded-sm transition-colors flex items-center justify-between gap-3 ${
                      !c.available ? "border-zinc-800 bg-zinc-900/30 text-zinc-600 cursor-not-allowed"
                      : c.risk ? "border-orange-900/50 bg-orange-950/10 text-orange-100 hover:border-orange-600"
                      : "border-zinc-800 bg-zinc-900/40 text-zinc-100 hover:border-amber-600"}`}>
                    <span className="flex items-center gap-2 text-sm">
                      {c.risk && c.available && <AlertTriangle size={13} className="text-orange-400 shrink-0" />}
                      {c.label}
                      {!c.available && c.locked_reason && <span className="text-[10px] uppercase tracking-widest text-zinc-600">· {c.locked_reason}</span>}
                    </span>
                    {c.available && <ChevronRight size={15} className="text-zinc-600 shrink-0" />}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Completed → recap */}
      {run && run.status === "completed" && (
        <div data-testid="adventure-complete" className="max-w-2xl">
          <div className="border border-amber-900/30 bg-zinc-950 rounded-sm overflow-hidden">
            <div className="px-5 py-3 border-b border-amber-900/20 flex items-center gap-2">
              <h2 className="font-cinzel text-amber-300 tracking-wide">{run.story_title}</h2>
            </div>
            <div className="p-5">
              <p className="text-zinc-200 leading-relaxed">{run.ending}</p>
              {run.lore && <p className="text-sm text-amber-200/80 italic mt-3 border-l-2 border-amber-800/50 pl-3">{run.lore}</p>}
              <Rewards rewards={run.rewards} />
              <p className="text-[12px] text-zinc-500 mt-4">
                The sands shift again in <span className="text-zinc-300">{resetIn(data.daily_reset)}</span> — return then with another gladiator.
              </p>
              <div className="flex gap-2 mt-4">
                <button className={btn("ghost") + " flex-1"} onClick={() => navigate("/equipment")}>Visit the Blacksmith</button>
                <button className={btn("ghost") + " flex-1"} onClick={() => navigate("/roster")}>View Roster</button>
              </div>
              {data.admin_unlimited && (
                <button data-testid="adventure-play-again-btn" onClick={() => setData((d) => ({ ...d, run: null, can_start: true }))}
                  className={btn() + " w-full mt-3"}>
                  Play again (admin — no daily limit)
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
